import { z } from 'zod';
import { moneyCentsSchema, uuidSchema } from './common.js';
/** How often a phone plan renews. `month` is a calendar month (same day next
 *  month, clamped); `thirty_days` is exactly 30 days (t1258). */
export const SERVICE_PLAN_BILLING_INTERVALS = ['month', 'year', 'thirty_days'];
/** "Monthly" / "Yearly" / "Every 30 days" — for pickers. */
export function billingIntervalLabel(interval) {
    return interval === 'year' ? 'Yearly' : interval === 'thirty_days' ? 'Every 30 days' : 'Monthly';
}
/** "month" / "year" / "30 days" — for "$25.00 / month". */
export function billingIntervalUnit(interval) {
    return interval === 'thirty_days' ? '30 days' : interval;
}
/**
 * How a `plan` product is billed (t1077): the Stripe Price that recurring
 * billing charges, and what the plan includes. Edited on the product, kept in
 * `service_plans`.
 */
export const servicePlanBillingSchema = z.object({
    /** The Price created in the Stripe dashboard (`price_…`). Blank while the
     *  plan is still being set up — the kiosk does not offer it until it is set. */
    stripePriceId: z
        .string()
        .trim()
        .regex(/^price_[A-Za-z0-9]+$/, 'A Stripe price id looks like price_1AbC…')
        .max(120)
        .nullable(),
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    /** What the plan costs per interval, shown to customers. Stripe's Price is
     *  what is actually charged, so keep the two equal. */
    monthlyCents: moneyCentsSchema,
    includedMinutes: z.number().int().nonnegative().nullable(),
    includedTexts: z.number().int().nonnegative().nullable(),
});
/** Mirrors the `subscription_status` enum. */
export const SUBSCRIPTION_STATUSES = [
    'incomplete',
    'active',
    'past_due',
    'paused',
    'cancelled',
];
/** A phone plan on a customer's account, as staff see it (t1083). */
export const adminSubscriptionSchema = z.object({
    id: uuidSchema,
    customerId: uuidSchema,
    customerName: z.string().nullable(),
    customerPhone: z.string().nullable(),
    productId: uuidSchema,
    planName: z.string(),
    /** t1309: who the line is for (Mrs Smith…), to tell a customer's lines apart. */
    label: z.string().nullable(),
    phoneLine: z.string().nullable(),
    simIccid: z.string().nullable(),
    /** t0801: who provides the service (US Mobile…), the handset, the day it was
     *  switched on, and what the shop pays per interval. */
    provider: z.string().nullable(),
    device: z.string().nullable(),
    activatedAt: z.string().nullable(),
    costCents: moneyCentsSchema.nullable(),
    /** The plan's price minus our cost per interval; null until a cost is entered. */
    profitCents: z.number().int().nullable(),
    status: z.enum(SUBSCRIPTION_STATUSES),
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    monthlyCents: moneyCentsSchema,
    /** When the paid period ends — the renewal date, or the end date once the
     *  plan is set to cancel. Null until the plan is activated. */
    currentPeriodEnd: z.iso.datetime().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    cancelledAt: z.iso.datetime().nullable(),
    /** The order the plan was bought on; null for a kiosk sign-up. */
    orderId: uuidSchema.nullable(),
    /** Whether Stripe will charge the renewal (false = local record only). */
    billedByStripe: z.boolean(),
    /** t1129: the coming charge was moved off Shabbos / Yom Tov, and to when. */
    chargeShiftKind: z.enum(['delayed', 'advanced', 'combined']).nullable(),
    chargeShiftAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const subscriptionListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    q: z.string().trim().max(200).optional(),
    /** 'except_cancelled' matches every status but cancelled, like rentals. */
    status: z.enum([...SUBSCRIPTION_STATUSES, 'except_cancelled']).optional(),
    customerId: uuidSchema.optional(),
});
const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-11-05');
export const updateSubscriptionSchema = z.object({
    label: z.string().trim().max(80).nullable().optional(),
    phoneLine: z.string().trim().max(30).nullable().optional(),
    simIccid: z.string().trim().max(40).nullable().optional(),
    /** t0801: who provides the service, the handset, when it was switched on and
     *  what the shop pays per interval. */
    provider: z.string().trim().max(60).nullable().optional(),
    device: z.string().trim().max(120).nullable().optional(),
    activatedAt: dateOnlySchema.nullable().optional(),
    costCents: moneyCentsSchema.nullable().optional(),
    /** t1257: the day the plan is next billed (YYYY-MM-DD) — or, for a one-time
     *  plan, the day it ends. */
    nextBillingDate: z.iso.date().optional(),
    /** t1257: false makes the plan one-time (no renewal after the billing date);
     *  true puts a one-time plan back on renewing. */
    renews: z.boolean().optional(),
});
/** t1381: move a hand-billed plan onto one of the customer's saved cards. */
export const billWithCardInputSchema = z.object({
    paymentMethodId: uuidSchema,
});
/** A plan set up outside this system, recorded by staff. Nothing is charged. */
export const createManualSubscriptionSchema = z.object({
    customerId: uuidSchema,
    productId: uuidSchema,
    label: z.string().trim().max(80).nullable().optional(),
    phoneLine: z.string().trim().max(30).nullable().optional(),
    simIccid: z.string().trim().max(40).nullable().optional(),
    provider: z.string().trim().max(60).nullable().optional(),
    device: z.string().trim().max(120).nullable().optional(),
    activatedAt: dateOnlySchema.nullable().optional(),
    costCents: moneyCentsSchema.nullable().optional(),
    renewsAt: dateOnlySchema,
});
/** t1380: a plan added without charging now — Stripe bills the saved card from
 *  `firstChargeDate`, then every interval. */
export const startDeferredSubscriptionSchema = z.object({
    customerId: uuidSchema,
    productId: uuidSchema,
    paymentMethodId: uuidSchema,
    firstChargeDate: dateOnlySchema,
    label: z.string().trim().max(80).nullable().optional(),
    phoneLine: z.string().trim().max(30).nullable().optional(),
    simIccid: z.string().trim().max(40).nullable().optional(),
    simUnitId: uuidSchema.nullable().optional(),
    provider: z.string().trim().max(60).nullable().optional(),
    device: z.string().trim().max(120).nullable().optional(),
    activatedAt: dateOnlySchema.nullable().optional(),
    costCents: moneyCentsSchema.nullable().optional(),
});
/** A plan staff can pick when adding one by hand. */
export const subscriptionPlanOptionSchema = z.object({
    productId: uuidSchema,
    name: z.string(),
    monthlyCents: moneyCentsSchema,
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    /** t1308: what selling the plan charges up front (its product price), and the
     *  tax category the sell-a-plan dialog previews the tax from. */
    priceCents: moneyCentsSchema,
    taxCategoryId: uuidSchema.nullable(),
});
/** t1379: a brand-new plan type defined inline from the Add plan dialog. */
export const createPlanOptionSchema = z.object({
    name: z.string().trim().min(1).max(200),
    /** What one period costs — both the up-front charge and the renewal amount. */
    priceCents: moneyCentsSchema.refine((v) => v > 0, 'A plan must cost more than $0'),
    billingInterval: z.enum(SERVICE_PLAN_BILLING_INTERVALS),
    taxCategoryId: uuidSchema.nullable().default(null),
});
export const cancelSubscriptionInputSchema = z.object({
    /** false (the default) lets the paid period run out; true ends it now. */
    immediately: z.boolean().default(false),
});
const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM');
/**
 * Keeps recurring plan charges off Shabbos and Yom Tov (t1129). Times are on
 * the support time zone's wall clock.
 */
const writableBillingCalendarSettingsSchema = z.object({
    enabled: z.boolean(),
    /** A charge pushed past Shabbos / Yom Tov runs at this time on the night it ends. */
    nightChargeTime: clockTimeSchema,
    /** A charge moved ahead of a Yom Tov runs at this time on the day before. */
    dayChargeTime: clockTimeSchema,
    /** A charge due from this time on the day before a restricted day counts as falling on it. */
    erevCutoff: clockTimeSchema,
    /** t1316: a customer's plans renewing the same day on the same card are charged as one payment. */
    combineSameDay: z.boolean(),
    /** t1374: email / text each customer an invoice before their plan charge. */
    chargeNoticeEmailEnabled: z.boolean(),
    chargeNoticeSmsEnabled: z.boolean(),
    /** How many days before the charge the notice goes out. */
    chargeNoticeLeadDays: z.number().int().min(1).max(14),
});
export const billingCalendarSettingsSchema = writableBillingCalendarSettingsSchema.extend({
    updatedAt: z.iso.datetime().nullable(),
});
export const updateBillingCalendarSettingsSchema = writableBillingCalendarSettingsSchema.partial();
//# sourceMappingURL=subscription.js.map